import { useEffect, useState, Suspense } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

function EmailConfirmContent() {
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('info')
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('Starting email confirmation process...')
        console.log('URL:', window.location.href)
        console.log('Search params:', Object.fromEntries(searchParams.entries()))

        // Get token and type from URL parameters
        const token = searchParams.get('token')
        const type = searchParams.get('type')
        const email = searchParams.get('email')

        if (!token || !type) {
          setMessage('Invalid confirmation link. Missing required parameters.')
          setMessageType('error')
          setLoading(false)
          return
        }

        // Verify email with backend API
        const getApiUrl = () => {
          const baseUrl = import.meta.env.VITE_API_URL;
          if (!baseUrl) {
            if (import.meta.env.DEV) return 'http://localhost:5001/api';
            console.error('VITE_API_URL is not set in production!');
            return '';
          }
          const cleanUrl = baseUrl.replace(/\/$/, '');
          return cleanUrl.endsWith('/api') ? cleanUrl : `${cleanUrl}/api`;
        };
        const apiUrl = getApiUrl()
        const response = await fetch(`${apiUrl}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            type,
            email
          })
        })

        const data = await response.json()

        if (!response.ok || !data.success) {
          console.error('Email verification error:', data.message)
          setMessage(data.message || 'Email verification failed. The link may be invalid or expired.')
          setMessageType('error')
          setLoading(false)
          return
        }

        // Success
        setMessage('Email verified successfully! You can now sign in to your account.')
        setMessageType('success')
        setLoading(false)

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth?message=Email verified successfully! Please sign in.')
        }, 3000)

      } catch (error) {
        console.error('Email confirmation error:', error)
        setMessage('An unexpected error occurred during email verification. Please try again.')
        setMessageType('error')
        setLoading(false)
      }
    }

    handleEmailConfirmation()
  }, [searchParams, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Email</h2>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center">
          {messageType === 'success' ? (
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ) : (
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          )}
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {messageType === 'success' ? 'Email Verified!' : 'Verification Failed'}
          </h2>
          
          <p className="text-gray-600 mb-6">{message}</p>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {messageType === 'success' ? 'Continue to Sign In' : 'Back to Sign In'}
            </button>
            
            {messageType === 'error' && (
              <button
                onClick={() => navigate('/auth/forgot-password')}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Request New Verification Email
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Loading component for Suspense fallback
function EmailConfirmLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading email confirmation...</p>
        </div>
      </div>
    </div>
  )
}

// Main component with Suspense boundary
export default function EmailConfirmPage() {
  return (
    <Suspense fallback={<EmailConfirmLoading />}>
      <EmailConfirmContent />
    </Suspense>
  )
}

